/**
 * 登录页 —— 兼「首次初始化管理员」。
 *
 * 打开时先问服务端 `/api/auth/state`：
 *   · initialized = false → 切到初始化模式（用户名 + 显示名 + 密码 + 确认密码），提示仅限本机；
 *   · initialized = true  → 普通登录（用户名 + 密码）。
 *
 * 成功后跳到 `location.state.from`（被守卫拦下来的原地址），没有就回首页。
 */
import { useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AuthError, getAuthState, initAdmin, login } from '../api/auth'
import { useAuth } from '../auth/AuthContext'

type Mode = 'loading' | 'login' | 'init' | 'unreachable'

interface LocationState {
  from?: string
}

/** 把服务端错误码翻成中文文案 */
function messageOf(err: unknown): string {
  if (!(err instanceof AuthError)) {
    return err instanceof Error ? err.message : String(err)
  }
  if (err.status === 0) return '无法连接服务端，请确认 Friday 服务已启动'
  if (err.status === 401) return '用户名或密码错误'
  if (err.status === 429) return '尝试次数过多，请稍后再试'
  if (err.status === 403) return '初始化仅限本机访问'
  if (err.status === 409) return '系统已经初始化过了，请直接登录'
  if (err.status === 404) return '服务端尚未提供认证接口（请更新 Friday 服务端后重试）'
  if (err.status === 400) return '用户名或密码不符合要求（密码至少 8 位）'
  return `操作失败（HTTP ${err.status}${err.code === '' ? '' : ` / ${err.code}`}）`
}

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, refresh } = useAuth()

  const from = useMemo(() => {
    const st = location.state as LocationState | null
    return st?.from ?? '/'
  }, [location.state])

  const [mode, setMode] = useState<Mode>('loading')
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  // 已经登录过就别停在登录页
  useEffect(() => {
    if (user !== null) navigate(from, { replace: true })
  }, [user, from, navigate])

  useEffect(() => {
    let alive = true
    getAuthState()
      .then((state) => {
        if (!alive) return
        if (state.authenticated) {
          void refresh().then(() => navigate(from, { replace: true }))
          return
        }
        setMode(state.initialized ? 'login' : 'init')
      })
      .catch((err: unknown) => {
        if (!alive) return
        setMode('unreachable')
        setError(messageOf(err))
      })
    return () => {
      alive = false
    }
  }, [from, navigate, refresh])

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    setError('')

    const name = username.trim()
    if (name === '') {
      setError('请输入用户名')
      return
    }
    if (mode === 'init') {
      if (password.length < 8) {
        setError('密码至少 8 位')
        return
      }
      if (password !== confirm) {
        setError('两次输入的密码不一致')
        return
      }
    }
    if (password === '') {
      setError('请输入密码')
      return
    }

    setBusy(true)
    try {
      if (mode === 'init') {
        const display = displayName.trim()
        await initAdmin({
          username: name,
          password,
          ...(display === '' ? {} : { displayName: display }),
        })
      } else {
        await login(name, password)
      }
      await refresh()
      navigate(from, { replace: true })
    } catch (err) {
      // 系统已经初始化过了：直接切回登录模式，省得用户困惑
      if (err instanceof AuthError && err.status === 409 && mode === 'init') {
        setMode('login')
      }
      setError(messageOf(err))
    } finally {
      setBusy(false)
    }
  }

  const isInit = mode === 'init'
  const title = isInit ? '初始化管理员' : '登录 Friday'
  const hint =
    mode === 'loading'
      ? '正在检查登录状态…'
      : mode === 'unreachable'
        ? '登录页需要服务端的认证接口，目前无法与之通信。'
        : isInit
          ? '这是第一次启动：创建一个本机管理员账号。初始化仅限本机访问，完成后此处会变成普通登录页。'
          : '请输入用户名和密码。'

  return (
    <div className="login">
      <form className="login__card" onSubmit={submit}>
        <div className="login__brand">
          <span className="login__logo">Friday</span>
          <span className="login__sub">本地看板</span>
        </div>

        <h1 className="login__title">{title}</h1>
        <p className="login__hint">{hint}</p>

        {mode === 'unreachable' && (
          <p className="login__err">
            {error !== '' ? error : '无法连接服务端：请确认 Friday 服务正在运行，然后刷新本页。'}
          </p>
        )}

        {mode !== 'loading' && mode !== 'unreachable' && (
          <>
            <label className="login__field">
              <span className="login__label">用户名</span>
              <input
                className="login__input"
                value={username}
                autoFocus
                autoComplete="username"
                disabled={busy}
                onChange={(e) => setUsername(e.target.value)}
              />
            </label>

            {isInit && (
              <label className="login__field">
                <span className="login__label">显示名（可选）</span>
                <input
                  className="login__input"
                  value={displayName}
                  autoComplete="nickname"
                  disabled={busy}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </label>
            )}

            <label className="login__field">
              <span className="login__label">密码</span>
              <input
                className="login__input"
                type="password"
                value={password}
                autoComplete={isInit ? 'new-password' : 'current-password'}
                disabled={busy}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>

            {isInit && (
              <label className="login__field">
                <span className="login__label">确认密码</span>
                <input
                  className="login__input"
                  type="password"
                  value={confirm}
                  autoComplete="new-password"
                  disabled={busy}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </label>
            )}

            {error !== '' && <p className="login__err">{error}</p>}

            <button type="submit" className="login__submit" disabled={busy}>
              {busy ? '处理中…' : isInit ? '创建管理员并登录' : '登录'}
            </button>
          </>
        )}
      </form>
    </div>
  )
}
